import { effect, inject, Injectable } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { base64Decode, base64Encode, isIsoDate } from '@softline/core';
import { createDateRangeFilter, readDateRangeFilter } from '@softapps/core/filter';
import { SOFTLINE_FILTER_WWS_ABC_ANALYSIS } from '../abc-analysis.api';
import { AbcAnalysisBaseMeasure } from '../types/abc-analysis-base-measure';
import { AbcAnalysisFilterRow } from '../types/abc-analysis-filter-row';
import { AbcAnalysisFilterStateService } from './abc-analysis-filter-state.service';

const ABC_ANALYSIS_BASE_MEASURES: readonly AbcAnalysisBaseMeasure[] = [
  'CONTRIBUTION_MARGIN',
  'NET',
  'QUANTITY',
];

function isAbcAnalysisBaseMeasure(value: string | null): value is AbcAnalysisBaseMeasure {
  return !!value && (ABC_ANALYSIS_BASE_MEASURES as readonly string[]).includes(value);
}

// Both AbcAnalysisFilterStateService and SOFTLINE_FILTER_WWS_ABC_ANALYSIS are plain in-memory
// signals with no persistence, so a reload or a shared/bookmarked link always falls back to
// hard-coded defaults. This mirrors that state into the URL's query params instead: hydrated
// once from whatever the section was entered with, then kept in sync on every change.
@Injectable()
export class AbcAnalysisFilterUrlSyncService {
  private readonly filterState = inject(AbcAnalysisFilterStateService);
  private readonly entityFilterState = inject(SOFTLINE_FILTER_WWS_ABC_ANALYSIS);
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);

  constructor() {
    const params = this.route.snapshot.queryParamMap;

    const baseMeasure = params.get('baseMeasure');
    if (isAbcAnalysisBaseMeasure(baseMeasure)) this.filterState.baseMeasure.set(baseMeasure);

    const abcAnalysisId = Number(params.get('abcAnalysisId'));
    if (Number.isInteger(abcAnalysisId) && abcAnalysisId > 0) {
      this.filterState.abcAnalysisId.set(abcAnalysisId);
    }

    const filter = params.get('filter');
    if (filter) {
      try {
        this.entityFilterState.set(JSON.parse(base64Decode(filter)));
      } catch {
        // Malformed/stale link — ignore and keep the default filter state.
      }
    }
    // links from before the period became a filter item carried it beside `filter` as two params
    const dateFrom = params.get('dateFrom');
    const dateTo = params.get('dateTo');
    if (isIsoDate(dateFrom) && isIsoDate(dateTo)) {
      const legacyPeriod = createDateRangeFilter<AbcAnalysisFilterRow>('period', { from: dateFrom, to: dateTo });
      if (!filter) this.entityFilterState.set(legacyPeriod);
      else if (!readDateRangeFilter(this.entityFilterState.state(), 'period')) this.entityFilterState.add(legacyPeriod);
    }

    effect(() => {
      const queryParams: Record<string, string | null> = {
        baseMeasure: this.filterState.baseMeasure(),
        abcAnalysisId: String(this.filterState.abcAnalysisId()),
        dateFrom: null,
        dateTo: null,
        filter: this.entityFilterState.state()
          ? base64Encode(JSON.stringify(this.entityFilterState.state()))
          : null,
      };
      this.router.navigate([], {
        queryParams,
        queryParamsHandling: 'merge',
        relativeTo: this.route,
      });
    });
  }
}
